import * as React from 'react'
import { ReactElement, RefObject, useEffect, useMemo, useRef } from 'react'
import { DialogContent, InfoLabel, LabelProps, makeStyles, mergeClasses, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { HammockFormDialogProps } from '../HammockFormDialog'
import * as Yup from 'yup'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { FixedPriceMutationDto, FixedPriceOverviewDto, TravelMutationDto } from '../../../../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import { InvoiceStatus } from '../../../../config/finance/invoiceConfig'
import { useFormikContext } from 'formik'
import FormikTextareaField from '../../../../components/Formik/FormikTextareaField'
import FormikInputField from '../../../../components/Formik/FormikInputField'
import FormikRichTextField from '../../../../components/Formik/FormikRichTextField'
import UserCombobox from '../../../../components/Combobox/UserCombobox'
import useRoles, { rolesWriteAllTimes } from '../../../../hooks/useRoles'
import { SubComboboxProps } from '../../../../components/Combobox/BaseCombobox'
import { UserListItemDto } from '../../../../config/userConfig'
import { useFormikFieldStyles } from '../../../../components/Formik/FormikField'
import { usePrevious } from '@fluentui/react-hooks'
import { IRefObject } from '@fluentui/utilities'
import { ITextField } from '@fluentui/react'
import useCurrency from '../../../../hooks/useCurrency'
import FormikMoneyField from '../../../../components/Formik/FormikMoneyField'
import FormContainer from '../../Form/FormContainer'
import useDerivedFormValues from '../../../hooks/useDerivedFormValues'
import useFixedPriceTypes from '../../../../hooks/useFixedPriceTypes'
import useVatTypes from '../../../../hooks/useVatTypes'
import { VatType } from '../../../../config/i18nConfig'
import { multiplyRate } from '../../../../helpers/numberHelper'
import FixedPriceTypeCombobox, { FixedPriceTypeComboboxProps } from '../../../../components/Combobox/FixedPriceTypeCombobox'
import VatTypeCombobox from '../../../../components/Combobox/i18n/VatTypeCombobox'
import FileCombobox, { FileComboboxProps } from '../../../../components/Combobox/FileCombobox'
import FormikDatePickerField from '../../../../components/Formik/FormikDatePickerField'

const useStyles = makeStyles({
    billable: {
        flexGrow: 2,
    },
    rateRow: {
        display: 'grid',
        // Without this the fields keep Fluent's intrinsic min-width and the dialog scrolls sideways in a task pane.
        '& > *': { minWidth: 0 },
        '& .fui-Field *': { minWidth: 0 },
        gridTemplateColumns: `1fr 1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRow: {
        display: 'grid',
        gridTemplateColumns: `2fr 3fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRowSmall: {
        display: 'grid',
        gridTemplateColumns: `1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
})

export const getValidationSchema = (translate: (key: string, options?: any) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        fileId: Yup.string().uuid().label(translate('registration.fileid')).required(),
        userId: Yup.string().uuid().label(translate('registration.userid')).required(),
        type: Yup.string().uuid().label(translate('fixedprice.type')).required(),
        name: Yup.string().label(translate('fixedprice.name')).required(),
        description: Yup.string().label(translate('registration.description')).required(),
        rate: Yup.string().label(translate('registration.rate')).required(),
        date: Yup.string().label(translate('registration.date')).required(),
    })
}

export default function FixedPriceMutationDialogBodyContent(
    props: HammockFormDialogProps<FixedPriceMutationDto, BaseMutableOverviewDialogProps<FixedPriceOverviewDto>> & {
        focusTrigger?: number
        updateFocusTrigger?: () => void
    },
): ReactElement {
    const roles = useRoles()
    const styles = useStyles()
    const formikFieldStyles = useFormikFieldStyles()
    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const { initialValues, values, setValues, setFieldValue } = useFormikContext<FixedPriceMutationDto>()
    const { applyDerivedValues, onKeyDownCapture } = useDerivedFormValues<FixedPriceMutationDto>()
    const previousValues = usePrevious<FixedPriceMutationDto>(values)

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as BaseMutableOverviewDialogProps<FixedPriceOverviewDto>), [props])

    const viewportSmall = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    const isInvoiceDraft: boolean = dialogBodyProps.item?.invoice?.status === InvoiceStatus.Draft || false
    const isInvoiceFinal: boolean =
        dialogBodyProps.item?.invoice?.status !== undefined && dialogBodyProps.item?.invoice?.status !== InvoiceStatus.Draft

    const { currency } = useCurrency(values.currency)
    const currencySymbol = useMemo<string>(() => currency?.symbol ?? currency?.alpha ?? '¤', [currency])

    const { loaded: fixedPriceTypesLoaded, fixedPriceTypes, getFixedPriceType } = useFixedPriceTypes()
    const { getVatType } = useVatTypes()
    const vatType = useMemo<VatType>(() => getVatType(values.vatType), [values.vatType])
    const fixedPriceType = useMemo(() => getFixedPriceType(values.type), [values.type, fixedPriceTypes])

    //Check if the category field should use the initial value or live value
    const useInitialValues = useMemo<boolean>(() => !previousValues, [initialValues, previousValues])

    const fileFocusRef: IRefObject<HTMLInputElement> = useRef<HTMLInputElement>()
    const typeFocusRef: IRefObject<HTMLInputElement> = useRef<HTMLInputElement>()
    const descriptionFocusRef: RefObject<ITextField> = useRef<ITextField>()
    const rateFocusRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isInvoiceDraft || isInvoiceFinal) {
            return
        }

        if (!values.fileId) {
            fileFocusRef.current?.focus()
        } else if (fixedPriceTypes.length > 0 && !values.type) {
            typeFocusRef.current?.focus()
        } else if (!!initialValues.description || !values.description) {
            descriptionFocusRef.current?.focus()
        } else {
            rateFocusRef.current?.focus()
        }
    }, [values.fileId, values.type, props.focusTrigger, fixedPriceTypesLoaded])

    useEffect(() => {
        props.setValidationSchema?.(getValidationSchema(translate))
    }, [])

    useEffect(() => {
        let name = values.name
        let description = values.description
        let longDescription = values.longDescription
        if (useInitialValues) {
            name = initialValues.name
            description = initialValues.description
            longDescription = initialValues.longDescription
        }

        applyDerivedValues({ name, description, longDescription })
    }, [fixedPriceTypes, values.type])

    // Keep rate (excl. VAT) and rateTotal (incl. VAT) in sync via the VAT percentage, like the old inline row.
    // For an excl-VAT type (or no type) the entered rate drives the total; for an incl-VAT type the total drives the rate.
    useEffect(() => {
        if (!fixedPriceType || !fixedPriceType.includingVat) {
            const factor = (vatType?.percentage ?? 0) / 100 + 1
            applyDerivedValues({ rateTotal: multiplyRate(factor, { amount: values.rate, currency: values.currency }).amount })
        }
    }, [values.rate, vatType, fixedPriceType])

    useEffect(() => {
        if (!fixedPriceType?.includingVat) {
            return
        }

        // On edit only the excl-VAT rate is known, so seed the total from it instead of deriving the rate from an
        // empty total - that would zero out the amount the user came to edit.
        if (values.rateTotal === undefined && values.rate !== undefined) {
            const factor = (vatType?.percentage ?? 0) / 100 + 1
            applyDerivedValues({ rateTotal: multiplyRate(factor, { amount: values.rate, currency: values.currency }).amount })
            return
        }

        const factor = 100 / (100 + (vatType?.percentage ?? 0))
        applyDerivedValues({ rate: multiplyRate(factor, { amount: values.rateTotal, currency: values.currency }).amount })
    }, [values.rateTotal, vatType, fixedPriceType])

    return (
        <DialogContent onKeyDownCapture={onKeyDownCapture}>
            <FormContainer>
                {props.unsavedWarning && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('base.unsaved.dialog')}</MessageBarBody>
                    </MessageBar>
                )}
                {isInvoiceDraft && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('fixedprice.edit.form.invoice.draft')}</MessageBarBody>
                    </MessageBar>
                )}
                {isInvoiceFinal && (
                    <MessageBar intent={'error'} layout={'multiline'}>
                        <MessageBarBody>{translate('fixedprice.edit.form.invoice.final')}</MessageBarBody>
                    </MessageBar>
                )}

                <FormikComboboxField<TravelMutationDto, FileComboboxProps>
                    required
                    fieldName={'fileId'}
                    component={FileCombobox}
                    placeholderKey={'registration.fileid'}
                    componentProps={{
                        placeholderPrefixKey: '',
                        readOnly: isInvoiceFinal || isInvoiceDraft,
                        disabled: isInvoiceFinal || isInvoiceDraft,
                        componentRef: fileFocusRef,
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                <FormikComboboxField<FixedPriceMutationDto, FixedPriceTypeComboboxProps>
                    required={fixedPriceTypesLoaded && fixedPriceTypes?.length > 0}
                    fieldName={'type'}
                    component={FixedPriceTypeCombobox}
                    placeholderKey={'fixedprice.type'}
                    componentProps={{
                        placeholderPrefixKey: '',
                        selectedOptions: [values.type],
                        currency: values.currency,
                        date: values.date,
                        componentRef: typeFocusRef,
                        onOptionSelect: (_, data, selectedEntities) => {
                            const newFixedPriceType = selectedEntities?.[0]

                            setValues(oldValues => ({
                                ...oldValues,
                                type: data.selectedOptions?.[0],
                                vatType: newFixedPriceType?.vatType,
                                name: newFixedPriceType?.name,
                                description: newFixedPriceType?.description,
                                longDescription: newFixedPriceType?.longDescription,
                                rate: (newFixedPriceType?.includingVat ? undefined : newFixedPriceType?.rate) ?? '0.00',
                                rateTotal: newFixedPriceType?.includingVat ? newFixedPriceType?.rate : undefined,
                            }))
                        },
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                <FormikInputField<FixedPriceMutationDto>
                    required
                    fieldName="name"
                    renderLabel={{
                        children: (_: unknown, slotProps: LabelProps) => <InfoLabel {...slotProps} info={translate('fixedprice.edit.name.info')} />,
                    }}
                    placeholderKey={'fixedprice.name'}
                    componentProps={{
                        required: true,
                        readOnly: isInvoiceFinal,
                        disabled: isInvoiceFinal,
                    }}
                    className={formikFieldStyles.tinyLabel}
                />
                <FormikTextareaField<FixedPriceMutationDto>
                    required
                    fieldName="description"
                    renderLabel={{
                        children: (_: unknown, slotProps: LabelProps) => (
                            <InfoLabel {...slotProps} info={translate('registration.edit.description.info')} />
                        ),
                    }}
                    placeholderKey={'registration.description'}
                    componentProps={{
                        readOnly: isInvoiceFinal,
                        disabled: isInvoiceFinal,
                        ref: descriptionFocusRef,
                    }}
                    className={formikFieldStyles.tinyLabel}
                />
                <FormikRichTextField<FixedPriceMutationDto>
                    fieldName="longDescription"
                    renderLabel={{
                        children: (_: unknown, slotProps: LabelProps) => (
                            <InfoLabel {...slotProps} info={translate('fixedprice.edit.longdescription.info')} />
                        ),
                    }}
                    placeholderKey={'registration.description'}
                    componentProps={{
                        readOnly: isInvoiceFinal,
                        disabled: isInvoiceFinal,
                    }}
                    className={formikFieldStyles.tinyLabel}
                />
                <FormikComboboxField<FixedPriceMutationDto, SubComboboxProps<UserListItemDto>>
                    required
                    fieldName={'userId'}
                    component={UserCombobox}
                    placeholderKey={'registration.userid'}
                    componentProps={{
                        placeholderPrefixKey: '',
                        readOnly: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        disabled: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                <div className={styles.rateRow}>
                    <FormikMoneyField<FixedPriceMutationDto>
                        required
                        fieldName={'rate'}
                        placeholderKey={'registration.rate'}
                        componentProps={{
                            // For an incl-VAT type the rate is derived from the total, so it becomes display-only.
                            readOnly: isInvoiceFinal || !!fixedPriceType?.includingVat,
                            disabled: isInvoiceFinal,
                            tabIndex: fixedPriceType?.includingVat ? -1 : undefined,
                            appearance: fixedPriceType?.includingVat ? 'filled-darker' : undefined,
                            contentBefore: currencySymbol,

                            input: { ref: rateFocusRef },
                        }}
                    />
                    <FormikComboboxField
                        required
                        fieldName={'vatType'}
                        component={VatTypeCombobox}
                        placeholderKey={'base.vattypecombobox.placeholder'}
                        componentProps={{
                            placeholderPrefixKey: '',
                            onOptionSelect: (_event, data) => {
                                setFieldValue('vatType', data.selectedOptions?.[0])
                            },
                        }}
                    />

                    <FormikMoneyField<FixedPriceMutationDto>
                        required
                        fieldName={'rateTotal'}
                        componentProps={{
                            // For an incl-VAT type the user enters the total; otherwise it is derived from the rate.
                            readOnly: isInvoiceFinal || !fixedPriceType?.includingVat,
                            disabled: isInvoiceFinal,
                            tabIndex: fixedPriceType?.includingVat ? undefined : -1,
                            appearance: fixedPriceType?.includingVat ? undefined : 'filled-darker',
                            contentBefore: currencySymbol,
                        }}
                    />
                </div>
                <div className={mergeClasses(styles.dateRow, viewportSmall && styles.dateRowSmall)}>
                    <FormikDatePickerField<FixedPriceMutationDto>
                        required
                        fieldName={'date'}
                        placeholderKey={'registration.date'}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                        }}
                    />
                </div>
                {/*Source - https://stackoverflow.com/a/477699*/}
                <input type="submit" hidden />
            </FormContainer>
        </DialogContent>
    )
}
